import { useState, useEffect } from 'react'
import { Input } from './Input'
import { Button } from './Button'
import { Alert } from './Alert'
import { Modal } from './Modal'
import { ClientStatusStepper } from './ClientStatusStepper'
import { Client, ClientStatus } from '../services/clientService'
import { Matter } from '../services/matterService'
import { usePaymentService, Payment } from '../services/paymentService'
import { useTerminology } from '../contexts/TerminologyContext'

interface ClientDetailProps {
  client: Client
  matters: Matter[]
  onBack: () => void
  onStatusChange: (status: ClientStatus) => void
  onSelectMatter: (matterId: string) => void
}

const formatDate = (value?: string | null) => {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

const formatAmount = (cents: number) => `$${(cents / 100).toFixed(2)}`

// Mirrors clientStatusClasses in statusStyles.ts, but as a dot color for the
// dark header chip — the light pill backgrounds there wash out on navy.
const clientStatusDotClasses: Record<ClientStatus, string> = {
  submitted: 'bg-gray-400',
  review: 'bg-amber-400',
  approved: 'bg-emerald-400',
  processing: 'bg-blue-400',
  completed: 'bg-accent',
}

export const ClientDetail: React.FC<ClientDetailProps> = ({ client, matters, onBack, onStatusChange, onSelectMatter }) => {
  const terminology = useTerminology()
  const [payments, setPayments] = useState<Payment[]>([])
  const [paymentsLoading, setPaymentsLoading] = useState(false)
  const [paymentsError, setPaymentsError] = useState<string | null>(null)

  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentAmount, setPaymentAmount] = useState('50.00')
  const [paymentDescription, setPaymentDescription] = useState('Invoice payment')
  const [paymentSubmitting, setPaymentSubmitting] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)

  const { listPayments, createPayment } = usePaymentService()

  const loadPayments = async () => {
    setPaymentsLoading(true)
    setPaymentsError(null)
    try {
      const results = await listPayments(client.id)
      setPayments(results)
    } catch (err) {
      setPaymentsError(err instanceof Error ? err.message : 'Failed to load payments')
    } finally {
      setPaymentsLoading(false)
    }
  }

  useEffect(() => {
    loadPayments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id])

  const handleTakePayment = async (e: React.FormEvent) => {
    e.preventDefault()
    setPaymentError(null)

    const amountCents = Math.round(Number(paymentAmount) * 100)
    if (!amountCents || amountCents <= 0) {
      setPaymentError('Enter a valid amount')
      return
    }

    setPaymentSubmitting(true)
    try {
      const payment = await createPayment(client.id, {
        amount: amountCents,
        description: paymentDescription,
      })
      setPayments((prev) => [payment, ...prev])
      setShowPaymentModal(false)
    } catch (err) {
      setPaymentError(err instanceof Error ? err.message : 'Failed to take payment')
    } finally {
      setPaymentSubmitting(false)
    }
  }

  const clientName = client.fullName || `${client.firstName} ${client.lastName}`
  const linkedMatters = matters.filter((m) => m.primaryClientId === client.id || m.secondaryClientId === client.id)
  const totalCollectedCents = payments.reduce((sum, p) => sum + p.amount, 0)
  const payerLast4 = client.payerId ? client.payerId.replace(/[^a-zA-Z0-9]/g, '').slice(-4) : null
  const addressParts = [client.streetAddress, client.suburb, client.state, client.postcode].filter(Boolean)

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div
        className="px-6 sm:px-8 py-6 flex items-start justify-between gap-4"
        style={{ background: 'linear-gradient(135deg, #0F2742, #1F3F66)' }}
      >
        <div className="min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="text-xs font-medium text-white/60 hover:text-white mb-2 inline-flex items-center gap-1"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
            </svg>
            All {terminology.clients.toLowerCase()}
          </button>
          <h2 className="text-2xl font-semibold text-white truncate">{clientName}</h2>
          <p className="text-sm text-white/60 mt-1 truncate">
            {client.emailAddress}
            {client.mobileNumber && ` · ${client.mobileNumber}`}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <Button type="button" variant="danger" onClick={() => setShowPaymentModal(true)}>
            Take Payment
          </Button>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 text-white capitalize">
            <span className={`w-1.5 h-1.5 rounded-full ${clientStatusDotClasses[client.status] || 'bg-gray-400'}`} />
            {client.status}
          </span>
        </div>
      </div>

      <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Status</h3>
          <ClientStatusStepper status={client.status} onChange={onStatusChange} />

          <h3 className="text-sm font-semibold text-gray-900 mt-8 mb-3">Linked {terminology.matters}</h3>
          {linkedMatters.length === 0 ? (
            <p className="text-sm text-gray-500">No {terminology.matters.toLowerCase()} yet for this {terminology.client.toLowerCase()}</p>
          ) : (
            <div className="space-y-2">
              {linkedMatters.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => onSelectMatter(m.id)}
                  className="w-full flex items-center justify-between gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 text-left transition-colors"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {m.matterType} &middot; {m.propertyAddress}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Settlement: {m.settlementDate ? formatDate(m.settlementDate) : 'Not set'}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      m.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {m.status}
                  </span>
                </button>
              ))}
            </div>
          )}
          {linkedMatters.length > 0 && (
            <p className="text-xs text-gray-500 mt-2">
              Identity verification is tracked per {terminology.matter.toLowerCase()} — open a {terminology.matter.toLowerCase()} above to view or start it.
            </p>
          )}
        </div>

        <div className="bg-gray-50 rounded-lg border border-gray-200 p-5 h-fit">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">Contact</h3>
          <p className="text-gray-900">{clientName}</p>
          {addressParts.length > 0 && <p className="text-xs text-gray-500 mt-0.5">{addressParts.join(', ')}</p>}
          <p className="text-xs text-gray-500 mt-0.5">
            {payerLast4 ? `Pinch payer linked · •••• ${payerLast4}` : 'No Pinch payer linked'}
          </p>

          <div className="border-t border-gray-200 my-4" />

          <h3 className="text-sm font-semibold text-gray-900 mb-1">Latest Payment</h3>
          {paymentsError && <Alert type="error" className="mb-2">{paymentsError}</Alert>}
          {paymentsLoading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : payments[0] ? (
            <>
              <p className="text-2xl font-semibold text-accent">
                {formatAmount(payments[0].amount)}
                <span className="text-sm font-medium text-accent/80"> &middot; {payments[0].status || 'pending'}</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {payments[0].description || 'Payment'} via Pinch on {formatDate(payments[0].transactionDate) || formatDate(payments[0].createdAt)}
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-500">No payments yet</p>
          )}

          {payments.length > 1 && (
            <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-200">
              Total collected: <span className="font-medium text-gray-700">{formatAmount(totalCollectedCents)}</span> across {payments.length} payments
            </p>
          )}
        </div>
      </div>

      <Modal open={showPaymentModal} onClose={() => setShowPaymentModal(false)} title="Take Payment">
        <form onSubmit={handleTakePayment} className="space-y-4">
          {paymentError && <Alert type="error">{paymentError}</Alert>}
          <Input
            label="Amount (AUD)"
            type="number"
            step="0.01"
            min="0.01"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
          />
          <Input
            label="Description"
            value={paymentDescription}
            onChange={(e) => setPaymentDescription(e.target.value)}
          />
          <Button type="submit" className="w-full" disabled={paymentSubmitting}>
            {paymentSubmitting ? 'Charging...' : 'Take Payment'}
          </Button>
        </form>
      </Modal>
    </div>
  )
}
